import type { QueryClient } from '@tanstack/react-query'
import {
  GET_ALL_DESKTOP_SETTINGS_QUERY_KEY,
  type GetAllDesktopSettingsData
} from 'shared/api/containerApi'
import type { PersistedWidgetSpec } from 'shared/types/widgets'

import {
  apiWidgetToPersistedSpec,
  persistedSpecToApiWidget,
  selectDesktopWidgets
} from './desktopWidgetAdapter'

const desktopSettingsQueryKey = [GET_ALL_DESKTOP_SETTINGS_QUERY_KEY] as const

/**
 * Replaces selected widget rows while preserving other desktop settings,
 * unknown types, and supported rows hidden by singleton deduplication.
 */
export const replaceDesktopWidgets = (
  data: GetAllDesktopSettingsData,
  widgets: PersistedWidgetSpec[]
): GetAllDesktopSettingsData => {
  const nextIds = new Set(widgets.map(widget => widget.id))
  const selectedIds = new Set(
    selectDesktopWidgets(data).map(widget => widget.id)
  )
  const untouchedWidgets = data.widgets.filter(widget => {
    if (nextIds.has(widget.id)) return false
    return !selectedIds.has(widget.id)
  })
  const duplicateWidgets = untouchedWidgets
    .filter(widget => apiWidgetToPersistedSpec(widget) !== null)
    .map((widget, index) => ({
      ...widget,
      display_order: widgets.length + index + 1
    }))
  const unknownWidgets = untouchedWidgets.filter(
    widget => apiWidgetToPersistedSpec(widget) === null
  )
  const existingById = new Map(data.widgets.map(widget => [widget.id, widget]))

  return {
    ...data,
    widgets: [
      ...unknownWidgets,
      ...widgets.map(widget => {
        const nextWidget = persistedSpecToApiWidget(widget)
        const existing = existingById.get(widget.id)
        return {
          ...nextWidget,
          configuration: {
            ...(existing?.configuration ?? {}),
            ...nextWidget.configuration
          }
        }
      }),
      ...duplicateWidgets
    ]
  }
}

export const setDesktopWidgetsQueryData = (
  queryClient: QueryClient,
  widgets: PersistedWidgetSpec[]
): void => {
  queryClient.setQueryData<GetAllDesktopSettingsData>(
    desktopSettingsQueryKey,
    current => (current ? replaceDesktopWidgets(current, widgets) : current)
  )
}
